import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { FormControl, MenuItem, Select } from '@mui/material';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Box, Button, Typography } from 'hephaestus/web';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type UnrecognizedSecurity = {
  ticker: string;
  targetWeight: number;
  rowIndex: number;
};

export type AvailableSecurity = {
  id: string;
  ticker: string;
  name: string;
};

export type SecuritiesReviewDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (replacements: Record<string, string>) => void;
  unrecognizedSecurities: UnrecognizedSecurity[];
  availableSecurities: AvailableSecurity[];
  loading?: boolean;
  title?: string;
  description?: string;
  submitLabel?: string;
};

const GRID_COLUMNS = '1fr 40px 2fr 120px';
const ARROW_ICON_SIZE = 20;

export const SecuritiesReviewDialog = ({
  open,
  onClose,
  onSubmit,
  unrecognizedSecurities,
  availableSecurities,
  loading = false,
  title,
  description,
  submitLabel,
}: SecuritiesReviewDialogProps) => {
  const { t } = useTranslation(['modelPortfolios', 'shared']);
  const [replacements, setReplacements] = useState<Record<string, string>>({});

  useEffect(() => {
    const initial: Record<string, string> = {};
    unrecognizedSecurities.forEach((sec) => {
      initial[sec.ticker] = '';
    });
    setReplacements(initial);
  }, [unrecognizedSecurities]);

  const handleReplacementChange = (ticker: string, replacementId: string) => {
    setReplacements((prev) => ({
      ...prev,
      [ticker]: replacementId,
    }));
  };

  const allSecuritiesReplaced = unrecognizedSecurities.every(
    (sec) => replacements[sec.ticker] && replacements[sec.ticker] !== '',
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      data-testid='securities-review-dialog'
    >
      <DialogTitle onClose={onClose}>
        {title || t('modelPortfolios:importCsv.modalTitle', 'Import CSV file')}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
            {description ||
              t(
                'modelPortfolios:importCsv.reviewDescription',
                'The following securities are not available. Replace with Cash or another security to continue.',
              )}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID_COLUMNS,
              alignItems: 'center',
              gap: 2,
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant='labelSmall' sx={{ fontWeight: 'medium' }}>
              Unrecognized
            </Typography>
            <Box />
            <Typography variant='labelSmall' sx={{ fontWeight: 'medium' }}>
              Replacement
            </Typography>
            <Typography variant='labelSmall' sx={{ fontWeight: 'medium' }}>
              Target Weight
            </Typography>
          </Box>

          {unrecognizedSecurities.map((security) => (
            <Box
              key={security.ticker}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID_COLUMNS,
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant='bodyMedium' sx={{ fontWeight: 'medium' }}>
                {security.ticker}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <ArrowForwardIcon
                  sx={{ fontSize: ARROW_ICON_SIZE, color: 'text.secondary' }}
                />
              </Box>

              <FormControl
                fullWidth
                size='small'
                error={!replacements[security.ticker]}
              >
                <Select
                  value={replacements[security.ticker] || ''}
                  onChange={(e) =>
                    handleReplacementChange(security.ticker, e.target.value)
                  }
                  displayEmpty
                  error={!replacements[security.ticker]}
                >
                  <MenuItem value='' disabled>
                    Select
                  </MenuItem>
                  {availableSecurities.map((sec) => (
                    <MenuItem key={sec.id} value={sec.id}>
                      {sec.ticker} - {sec.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                {security.targetWeight}%
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogFooter>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            gap: 1,
          }}
        >
          <Button
            onClick={onClose}
            variant='contained'
            disabled={loading}
            data-testid='securities-review-cancel-button'
            color='secondary'
          >
            {t('shared:cancel')}
          </Button>
          <Button
            onClick={() => onSubmit(replacements)}
            disabled={!allSecuritiesReplaced || loading}
            loading={loading}
            data-testid='securities-review-submit-button'
          >
            {submitLabel ||
              t('modelPortfolios:importCsv.import', 'Import file')}
          </Button>
        </Box>
      </DialogFooter>
    </Dialog>
  );
};

export default SecuritiesReviewDialog;
